// Calendario slot manutenzione — griglia mensile con slot disponibili/occupati.
// Click su slot disponibile → callback di conferma prenotazione.
import { useState } from 'react'
import { ChevronLeft, ChevronRight, Check, X } from 'lucide-react'
import { cn } from '../../../lib/utils'

interface AvailableSlot {
  date: string    // YYYY-MM-DD
  available: boolean
}

interface SlotCalendarProps {
  slots: AvailableSlot[]
  currentMonth: string  // YYYY-MM
  onMonthChange: (month: string) => void
  onSlotSelect: (date: string) => void
  selectedDate?: string | null
  isLoading?: boolean
}

const GIORNI_SETTIMANA = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
const MESI = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
]

export function SlotCalendar({
  slots,
  currentMonth,
  onMonthChange,
  onSlotSelect,
  selectedDate,
  isLoading,
}: SlotCalendarProps) {
  const [yearStr, monthStr] = currentMonth.split('-')
  const year = parseInt(yearStr ?? '2026', 10)
  const month = parseInt(monthStr ?? '1', 10) - 1

  // Costruisce la griglia del calendario
  const firstDayOfMonth = new Date(year, month, 1)
  const lastDayOfMonth = new Date(year, month + 1, 0)
  const totalDays = lastDayOfMonth.getDate()

  // Giorno della settimana del primo giorno (0=domenica → convertiamo a lun=0)
  const startWeekDay = (firstDayOfMonth.getDay() + 6) % 7

  // Mappa slot per accesso rapido
  const slotMap = new Map(slots.map((s) => [s.date, s]))

  const navigateMonth = (direction: -1 | 1) => {
    const newDate = new Date(year, month + direction, 1)
    const newMonth = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}`
    onMonthChange(newMonth)
  }

  const prevMonth = `${year}-${String(month).padStart(2, '0')}`
  const today = new Date().toISOString().slice(0, 10)

  return (
    <div
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
      role="region"
      aria-label="Calendario slot disponibili"
    >
      {/* Header mese */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <button
          type="button"
          onClick={() => navigateMonth(-1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-40"
          disabled={currentMonth <= today.slice(0, 7)}
          aria-label="Mese precedente"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <h3 className="text-sm font-semibold text-gray-900">
          {MESI[month]} {year}
        </h3>
        <button
          type="button"
          onClick={() => navigateMonth(1)}
          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100"
          aria-label="Mese successivo"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      {/* Intestazione giorni settimana */}
      <div className="grid grid-cols-7 border-b border-gray-100">
        {GIORNI_SETTIMANA.map((g) => (
          <div
            key={g}
            className="py-2 text-center text-xs font-medium text-gray-400"
            aria-hidden="true"
          >
            {g}
          </div>
        ))}
      </div>

      {/* Griglia giorni */}
      <div className={cn('grid grid-cols-7', isLoading && 'opacity-50')}>
        {/* Celle vuote per allineare il primo giorno */}
        {Array.from({ length: startWeekDay }).map((_, i) => (
          <div key={`empty-${i}`} className="aspect-square" aria-hidden="true" />
        ))}

        {/* Celle giorni del mese */}
        {Array.from({ length: totalDays }).map((_, i) => {
          const day = i + 1
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
          const slot = slotMap.get(dateStr)
          const isAvailable = slot?.available === true
          const isSelected = selectedDate === dateStr
          const isPast = dateStr < today
          const isToday = dateStr === today

          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => isAvailable && onSlotSelect(dateStr)}
              disabled={!isAvailable || isLoading}
              className={cn(
                'flex aspect-square flex-col items-center justify-center gap-0.5 border border-transparent text-sm transition-all',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--fc-primary)]',
                isToday && !isSelected && 'font-bold text-[var(--fc-primary)]',
                isPast && 'cursor-not-allowed text-gray-200',
                !slot && !isPast && 'text-gray-300',
                slot && !isAvailable && !isPast && 'cursor-not-allowed text-gray-300 line-through',
                isAvailable && !isSelected && 'cursor-pointer rounded-lg text-gray-700 hover:bg-blue-50 hover:text-[var(--fc-primary)]',
                isSelected && 'rounded-lg bg-[var(--fc-primary)] font-semibold text-white',
              )}
              aria-label={
                isAvailable
                  ? `${dateStr} — slot disponibile`
                  : `${dateStr} — non disponibile`
              }
              aria-pressed={isSelected}
            >
              <span>{day}</span>
              {isAvailable && !isSelected && (
                <span
                  className="h-1 w-1 rounded-full bg-[var(--fc-success)]"
                  aria-hidden="true"
                />
              )}
              {isSelected && (
                <Check className="h-3 w-3" aria-hidden="true" />
              )}
            </button>
          )
        })}
      </div>

      {/* Legenda */}
      <div className="flex items-center gap-4 border-t border-gray-100 px-4 py-3" aria-label="Legenda">
        <div className="flex items-center gap-1.5">
          <span className="flex h-2 w-2 items-center justify-center">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--fc-success)]" aria-hidden="true" />
          </span>
          <span className="text-xs text-gray-500">Disponibile</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-300 line-through" aria-hidden="true">00</span>
          <span className="text-xs text-gray-500">Occupato</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="h-4 w-4 rounded bg-[var(--fc-primary)]" aria-hidden="true" />
          <span className="text-xs text-gray-500">Selezionato</span>
        </div>
      </div>
    </div>
  )
}
